import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Building2, MapPin, ShieldCheck, PhoneCall } from 'lucide-react';
import { useLang, LangToggle } from '@/lib/i18n';

/**
 * Public intake entry (`/intake/:pid`) — hit by patients who scan the practice
 * QR code or click the practice's public link.
 *
 * SAFETY STEP: before any submission is created, the patient sees the practice
 * identity card (name, address, phone) and must CONFIRM this is their doctor's
 * office. This prevents paperwork being requested from the wrong practice.
 * No staff names here — the endpoint behind it is public (GAPS S6).
 */
export default function IntakeRedirect() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [declined, setDeclined] = useState(false);
  // Guard against React 18 StrictMode double-mount (avoids double intake creation)
  const startedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(`/practice/${pid}/intake-info`)
      .then((r) => { if (alive) setInfo(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || t('intake.loadError')); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  const confirmAndStart = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStarting(true);
    try {
      const r = await api.post(`/practice/${pid}/intake`, {
        source: params.get('source') || 'qr',
        packet_type_id: params.get('packet_type_id') || undefined,
      });
      // Straight into the form: a fresh intake is always a draft, and the
      // welcome page would only redirect it there after a second fetch.
      navigate(`${r.data.redirect_to}/form`, { replace: true });
    } catch (e) {
      startedRef.current = false;
      setStarting(false);
      setError(e?.response?.data?.detail || t('intake.startError'));
    }
  };

  return (
    <div className="min-h-screen hero-wash grid place-items-center p-4 sm:p-6">
      <Card className="w-full max-w-md" data-testid="intake-confirm-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-lg">
              {error ? t('intake.linkIssue') : declined ? t('intake.wrongPracticeTitle') : t('intake.confirmTitle')}
            </CardTitle>
            <LangToggle />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <>
              <div className="flex items-start gap-2 text-sm text-[hsl(0_72%_40%)]" data-testid="intake-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
              <Button variant="outline" onClick={() => window.location.reload()} data-testid="intake-retry">{t('intake.tryAgain')}</Button>
            </>
          ) : declined ? (
            <div className="space-y-3" data-testid="intake-declined">
              <p className="text-sm text-muted-foreground">{t('intake.wrongPracticeBody')}</p>
              <div className="rounded-lg bg-secondary p-3 flex items-start gap-2 text-sm">
                <PhoneCall className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{t('intake.wrongPracticeHint')}</span>
              </div>
              <Button variant="outline" onClick={() => setDeclined(false)} data-testid="intake-back">{t('intake.goBack')}</Button>
            </div>
          ) : !info ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="intake-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('intake.loading')}
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{t('intake.confirmSubtitle')}</p>

              {/* Practice identity card */}
              <div className="rounded-xl border bg-card p-4 space-y-2.5" data-testid="intake-practice-card">
                <div className="flex items-center gap-2.5">
                  {info.logo ? (
                    <img src={info.logo} alt={`${info.name} logo`} className="h-10 w-auto max-w-[110px] object-contain shrink-0" data-testid="intake-practice-logo" />
                  ) : (
                    <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                      <Building2 className="h-4.5 w-4.5" />
                    </span>
                  )}
                  <p className="font-semibold leading-tight" data-testid="intake-practice-name">{info.name}</p>
                </div>
                {info.address && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="intake-practice-address">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {info.address}
                  </p>
                )}
                {info.practice_number && (
                  <p className="text-xs text-muted-foreground" data-testid="intake-practice-number">
                    {info.practice_number}
                  </p>
                )}
              </div>

              <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                <span>{t('intake.whyConfirm')}</span>
              </div>

              <div className="space-y-2 pt-1">
                <Button
                  className="w-full h-11"
                  onClick={confirmAndStart}
                  disabled={starting}
                  data-testid="intake-confirm-yes"
                >
                  {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {starting ? t('intake.starting') : t('intake.yesButton', { practice: info.name })}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setDeclined(true)}
                  disabled={starting}
                  data-testid="intake-confirm-no"
                >
                  {t('intake.noButton')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
